/**
 * Public "Verify my receipt" widget.
 * ---------------------------------------------------------------------------
 * Anyone — buyer, auditor, journalist — can paste a Solana transaction
 * signature and get the server-verified on-chain status, the matching ledger
 * entry (redacted) and a direct explorer link. All verification happens
 * server-side; nothing is trusted from the browser.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, CheckCircle2, Loader2, SearchCheck, ShieldAlert, SignalLow } from "lucide-react";
import { track } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n";
import { SIGNATURE_RE } from "@/lib/verify-receipt.shared";
import type { PublicReceipt } from "@/lib/verify-receipt.server";

const num = (v: number, d = 2) =>
  v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

function useReceiptLookup() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PublicReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (raw: string) => {
    const signature = raw.trim();
    if (!signature) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { verifyPublicReceipt } = await import("@/lib/verify-receipt.functions");
      const res = (await verifyPublicReceipt({ data: { signature } })) as PublicReceipt;
      setResult(res);
      track("receipt_verify_lookup", {
        verified: res.verified,
        status: res.status,
        in_ledger: res.sale !== null,
      });
      try {
        const { reportAuditEvent } = await import("@/lib/audit.functions");
        await reportAuditEvent({
          data: {
            event: "receipt_verified",
            signature,
            detail: { verified: res.verified, status: res.status },
          },
        });
      } catch {
        /* audit is best-effort */
      }
    } catch {
      setError("error");
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, result, error, check, reset: () => setResult(null) };
}

function ResultCard({ result }: { result: PublicReceipt }) {
  const { t } = useI18n();
  const tone = result.verified
    ? { Icon: CheckCircle2, color: "oklch(0.86 0.16 165)", label: t("verify.verified") }
    : result.unreachable
      ? { Icon: SignalLow, color: "oklch(0.86 0.14 90)", label: t("verify.unreachable") }
      : result.error === "invalid_signature"
        ? { Icon: ShieldAlert, color: "oklch(0.72 0.19 25)", label: t("verify.invalid") }
        : result.error === "rate_limited"
          ? { Icon: ShieldAlert, color: "oklch(0.86 0.14 90)", label: t("verify.rateLimited") }
          : { Icon: ShieldAlert, color: "oklch(0.72 0.19 25)", label: `${t("verify.notConfirmed")} (${result.status})` };

  return (
    <div
      data-testid="verify-receipt-result"
      className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4"
      role="status"
      aria-live="polite"
    >
      <p className="flex items-center gap-2 text-sm font-semibold" style={{ color: tone.color }}>
        <tone.Icon className="h-4 w-4" aria-hidden="true" />
        {tone.label}
      </p>

      <dl className="mt-3 grid gap-2 text-xs">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{t("verify.receiptId")}</dt>
          <dd className="font-mono">{result.receiptId}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{t("verify.signature")}</dt>
          <dd className="font-mono break-all text-right">
            {result.signature.slice(0, 10)}…{result.signature.slice(-10)}
          </dd>
        </div>
        {result.sale ? (
          <>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("verify.ledgerEntry")}</dt>
              <dd>{new Date(result.sale.createdAt).toLocaleString()}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("verify.purchase")}</dt>
              <dd>
                {num(result.sale.payAmount, 4)} {result.sale.payCurrency} →{" "}
                {num(result.sale.receiveAmount)} {result.sale.token}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("verify.value")}</dt>
              <dd>${num(result.sale.usdValue)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("verify.wallet")}</dt>
              <dd className="font-mono">{result.sale.walletMasked}</dd>
            </div>
          </>
        ) : (
          <div className="text-muted-foreground">{t("verify.noLedger")}</div>
        )}
      </dl>

      {(result.checks?.length ?? 0) > 0 && (
        <ul className="mt-3 space-y-1 text-[11px]" aria-label={t("verify.checks")}>
          {(result.checks ?? []).map((c) => (
            <li key={c.id} className="flex items-start gap-2">
              <span aria-hidden="true" style={{ color: c.ok ? "oklch(0.86 0.16 165)" : "oklch(0.72 0.19 25)" }}>
                {c.ok ? "✓" : "✕"}
              </span>
              <span className="text-muted-foreground">{c.detail}</span>
            </li>
          ))}
        </ul>
      )}

      {result.replay && <p className="mt-3 text-[11px] text-muted-foreground">{t("verify.replay")}</p>}

      <a
        href={result.explorerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-saffron hover:underline"
      >
        {t("verify.explorer")} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </div>
  );
}

function LookupForm({ initialSignature = "", idPrefix }: { initialSignature?: string; idPrefix: string }) {
  const { t } = useI18n();
  const [signature, setSignature] = useState(initialSignature);
  const [touched, setTouched] = useState(false);
  const { busy, result, error, check } = useReceiptLookup();
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => setSignature(initialSignature), [initialSignature]);

  const trimmed = signature.trim();
  const formatInvalid = touched && trimmed.length > 0 && !SIGNATURE_RE.test(trimmed);
  const errId = `${idPrefix}-err`;

  // Move screen-reader focus to the outcome as soon as it renders.
  useEffect(() => {
    if (result || error) resultRef.current?.focus();
  }, [result, error]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (!SIGNATURE_RE.test(trimmed)) return;
        void check(trimmed);
      }}
      noValidate
    >
      <label
        htmlFor={`${idPrefix}-sig`}
        className="block text-xs uppercase tracking-wider text-muted-foreground"
      >
        {t("verify.label")}
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          id={`${idPrefix}-sig`}
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          onBlur={() => setTouched(true)}
          spellCheck={false}
          autoComplete="off"
          inputMode="text"
          aria-invalid={formatInvalid || undefined}
          aria-describedby={formatInvalid ? errId : undefined}
          placeholder={t("verify.placeholder")}
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
        />
        <button
          type="submit"
          disabled={busy || trimmed.length === 0}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-saffron to-coral px-5 py-2.5 text-sm font-semibold text-obsidian focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {busy ? t("verify.busy") : t("verify.submit")}
        </button>
      </div>
      {formatInvalid && (
        <p id={errId} className="mt-3 text-xs text-coral" role="alert">
          {t("verify.invalid")}
        </p>
      )}
      <div ref={resultRef} tabIndex={-1} className="focus-visible:outline-none">
        {error && (
          <p className="mt-3 text-xs text-coral" role="alert">
            {t("verify.error")}
          </p>
        )}
        {result && <ResultCard result={result} />}
      </div>
    </form>
  );
}

/** Full public section, rendered on the landing page. */
export function VerifyReceiptSection() {
  const { t } = useI18n();
  return (
    <section id="verify-receipt" className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-saffron">
          <SearchCheck className="h-4 w-4" aria-hidden="true" /> {t("verify.badge")}
        </p>
        <h2 className="mt-3 font-display text-2xl font-medium tracking-tight sm:text-3xl">
          {t("verify.title")}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("verify.intro")}</p>
        <div className="mt-6">
          <LookupForm idPrefix="verify-section" />
        </div>
      </div>
    </section>
  );
}

/** Compact modal used from the purchase confirmation screen. */
export function VerifyReceiptModal({
  open,
  onClose,
  initialSignature = "",
}: {
  open: boolean;
  onClose: () => void;
  initialSignature?: string;
}) {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const id = window.setTimeout(() => closeRef.current?.focus(), 40);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-receipt-title"
        data-testid="verify-receipt-modal"
        className="w-full max-w-lg rounded-t-3xl border border-white/10 bg-background p-6 sm:rounded-3xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="verify-receipt-title" className="text-base font-bold tracking-tight">
              {t("verify.title")}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("verify.modalIntro")}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("verify.close")}
            className="shrink-0 rounded-full border border-white/10 px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>
        <div className="mt-4">
          <LookupForm idPrefix="verify-modal" initialSignature={initialSignature} />
        </div>
      </div>
    </div>
  );
}

/** Button that opens the verification modal (purchase confirmation screen). */
export function VerifyReceiptTrigger({ signature }: { signature?: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="verify-receipt-trigger"
        className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60 sm:min-h-9"
      >
        <SearchCheck className="h-3.5 w-3.5" aria-hidden="true" /> {t("verify.title")}
      </button>
      <VerifyReceiptModal open={open} onClose={() => setOpen(false)} initialSignature={signature} />
    </>
  );
}
